import React, { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { Link } from "react-router-dom";
import { auth, db, storage } from "../config/firebase";
import "./AdminProjects.scss";

const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;
const EMPTY_FORM = {
  heading: "",
  url: "https://",
  tag: "",
  description: "",
  imageUrl: "",
  order: "0",
  published: true,
};

const toSafeFileName = (name) => name.toLowerCase().replace(/[^a-z0-9.-]+/g, "-");

function SignInPanel({ onError }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    onError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (error) {
      onError("Sign-in failed. Check your email and password, and confirm Email/Password sign-in is enabled in Firebase Authentication.");
      console.error("Admin sign-in failed", error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-signin" onSubmit={handleSubmit}>
      <label htmlFor="admin-email">Email</label>
      <input id="admin-email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required />
      <label htmlFor="admin-password">Password</label>
      <input id="admin-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
      <button type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
    </form>
  );
}

function ProjectForm({ project, onCancel, onSave }) {
  const [form, setForm] = useState(project || EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const uploadImage = (file, projectId) => new Promise((resolve, reject) => {
    const path = `portfolio-projects/${auth.currentUser.uid}/${projectId}/${Date.now()}-${toSafeFileName(file.name)}`;
    const task = uploadBytesResumable(ref(storage, path), file, { contentType: file.type });

    task.on(
      "state_changed",
      (snapshot) => setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      reject,
      async () => {
        try {
          resolve({ imageUrl: await getDownloadURL(task.snapshot.ref), imagePath: path });
        } catch (uploadError) {
          reject(uploadError);
        }
      },
    );
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (imageFile && (!imageFile.type.startsWith("image/") || imageFile.size > 8 * 1024 * 1024)) {
      setError("Choose an image smaller than 8 MB.");
      return;
    }
    if (!imageFile && !form.imageUrl.trim()) {
      setError("Add an image URL or choose an image file to upload.");
      return;
    }

    setBusy(true);
    try {
      const projectRef = project?.id
        ? doc(db, "portfolioProjects", project.id)
        : doc(collection(db, "portfolioProjects"));
      let imageUrl = form.imageUrl.trim();
      let imagePath = form.imagePath || "";

      if (imageFile) {
        const uploadedImage = await uploadImage(imageFile, projectRef.id);
        imageUrl = uploadedImage.imageUrl;
        imagePath = uploadedImage.imagePath;
      }

      await setDoc(projectRef, {
        heading: form.heading.trim(),
        url: form.url.trim(),
        tag: form.tag.trim(),
        description: form.description.trim(),
        imageUrl,
        imagePath,
        order: Number.parseInt(form.order, 10) || 0,
        published: Boolean(form.published),
        createdAt: project?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await setDoc(doc(db, "siteContent", "projects"), {
        initialized: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      if (imageFile && project?.imagePath && project.imagePath !== imagePath) {
        try {
          await deleteObject(ref(storage, project.imagePath));
        } catch (cleanupError) {
          console.warn("The old project image could not be removed", cleanupError);
        }
      }

      onSave();
    } catch (saveError) {
      console.error("Could not save portfolio project", saveError);
      setError("Could not save this project. Check your Firestore and Storage rules and try again.");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <form className="admin-project-form" onSubmit={handleSubmit}>
      <h2>{project ? "Edit project" : "Add a project"}</h2>
      <label htmlFor="project-heading">Project title</label>
      <input id="project-heading" name="heading" value={form.heading} onChange={updateField} maxLength={100} required />

      <label htmlFor="project-url">Project website</label>
      <input id="project-url" name="url" type="url" value={form.url} onChange={updateField} maxLength={500} required />

      <label htmlFor="project-tag">Category</label>
      <input id="project-tag" name="tag" value={form.tag} onChange={updateField} maxLength={80} required />

      <label htmlFor="project-description">Description</label>
      <textarea id="project-description" name="description" rows="3" value={form.description || ""} onChange={updateField} maxLength={600} />

      <label htmlFor="project-image-url">Image URL</label>
      <input id="project-image-url" name="imageUrl" type="text" inputMode="url" value={form.imageUrl} onChange={updateField} placeholder="https://…" />

      <label htmlFor="project-image-file">Or upload an image (max 8 MB)</label>
      <input id="project-image-file" type="file" accept="image/*" onChange={(event) => setImageFile(event.target.files?.[0] || null)} />
      {progress > 0 && <progress className="admin-upload-progress" value={progress} max="100">{progress}%</progress>}

      <div className="admin-form-row">
        <div>
          <label htmlFor="project-order">Display order</label>
          <input id="project-order" name="order" type="number" min="0" step="1" value={form.order} onChange={updateField} required />
        </div>
        <label className="admin-checkbox">
          <input name="published" type="checkbox" checked={Boolean(form.published)} onChange={updateField} />
          Published on website
        </label>
      </div>

      {error && <p className="admin-message admin-message--error" role="alert">{error}</p>}
      <div className="admin-form-actions">
        <button type="submit" disabled={busy}>{busy ? "Saving…" : "Save project"}</button>
        <button type="button" className="admin-button-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

export default function AdminProjects() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsReady, setProjectsReady] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => onAuthStateChanged(auth, (currentUser) => {
    setUser(currentUser);
    setAuthReady(true);
  }), []);

  useEffect(() => {
    if (!user || !ADMIN_UID || user.uid !== ADMIN_UID) {
      setProjects([]);
      setProjectsReady(false);
      return undefined;
    }

    const projectsQuery = query(collection(db, "portfolioProjects"), orderBy("order", "asc"));
    return onSnapshot(
      projectsQuery,
      (snapshot) => {
        setProjects(snapshot.docs.map((projectDoc) => ({ id: projectDoc.id, ...projectDoc.data() })));
        setProjectsReady(true);
        setError("");
      },
      (loadError) => {
        console.error("Could not load admin project list", loadError);
        setError("Could not load projects. Confirm the admin UID and Firestore rules setup.");
        setProjectsReady(true);
      },
    );
  }, [user]);


  const handleDelete = async (project) => {
    if (!window.confirm(`Delete “${project.heading}”? This cannot be undone.`)) return;
    setError("");
    setMessage("");
    try {
      await deleteDoc(doc(db, "portfolioProjects", project.id));
      if (project.imagePath) {
        try {
          await deleteObject(ref(storage, project.imagePath));
        } catch (cleanupError) {
          console.warn("The project image could not be removed", cleanupError);
        }
      }
      setMessage("Project deleted.");
    } catch (deleteError) {
      console.error("Could not delete portfolio project", deleteError);
      setError("Could not delete this project. Check your Firestore rules and try again.");
    }
  };

  if (!authReady) {
    return <main className="admin-page"><p className="admin-loading">Loading admin access…</p></main>;
  }

  return (
    <main className="admin-page">
      <div className="admin-shell">
        <header className="admin-header">
          <div>
            <p className="admin-eyebrow">Portfolio management</p>
            <h1>Projects</h1>
          </div>
          <div className="admin-header-actions">
            <Link to="/" className="admin-home-link">View website</Link>
            {user && <button className="admin-button-secondary" type="button" onClick={() => signOut(auth)}>Sign out</button>}
          </div>
        </header>

        {!user && (
          <section className="admin-panel">
            <h2>Sign in to manage projects</h2>
            <p>This page is for the site owner. Visitor accounts cannot edit portfolio content.</p>
            <SignInPanel onError={setError} />
          </section>
        )}

        {user && !ADMIN_UID && (
          <section className="admin-panel">
            <h2>Finish one-time admin setup</h2>
            <p>Add your Firebase user ID to <code>REACT_APP_ADMIN_UID</code> in your local environment and in the Firestore and Storage rules, then rebuild the site.</p>
            <p className="admin-uid-label">Your Firebase user ID</p>
            <code className="admin-uid">{user.uid}</code>
            <p>Keep this account private. Don’t enable public sign-up for the admin tool.</p>
          </section>
        )}

        {user && ADMIN_UID && user.uid !== ADMIN_UID && (
          <section className="admin-panel">
            <h2>Access denied</h2>
            <p>This signed-in account is not the configured project owner account.</p>
          </section>
        )}

        {user && ADMIN_UID && user.uid === ADMIN_UID && (
          <>
            <section className="admin-panel">
              <div className="admin-toolbar">
                <div>
                  <h2>Your project cards</h2>
                  <p>Changes are saved to Firestore and appear on the website without a new deployment.</p>
                </div>
                {!creatingProject && !editingProject && (
                  <button type="button" onClick={() => { setCreatingProject(true); setMessage(""); setError(""); }}>Add project</button>
                )}
              </div>

              {error && <p className="admin-message admin-message--error" role="alert">{error}</p>}
              {message && <p className="admin-message admin-message--success" role="status">{message}</p>}

              {!projectsReady && <p className="admin-loading">Loading projects…</p>}

              {projectsReady && projects.length === 0 && !creatingProject && !editingProject && (
                <div className="admin-empty-state">
                  <p>No projects found. Add your first project using the button above.</p>
                </div>
              )}

              {(creatingProject || editingProject) && (
                <ProjectForm
                  key={editingProject?.id || "new-project"}
                  project={editingProject}
                  onCancel={() => { setCreatingProject(false); setEditingProject(null); }}
                  onSave={() => {
                    setCreatingProject(false);
                    setEditingProject(null);
                    setMessage("Project saved.");
                  }}
                />
              )}

              {projects.length > 0 && !creatingProject && !editingProject && (
                <div className="admin-project-list">
                  {projects.map((project) => (
                    <article className="admin-project-row" key={project.id}>
                      <img src={project.imageUrl} alt="" />
                      <div className="admin-project-copy">
                        <h3>{project.heading}</h3>
                        <p>{project.tag} · Order {project.order} · {project.published ? "Published" : "Draft"}</p>
                      </div>
                      <div className="admin-row-actions">
                        <button type="button" className="admin-button-secondary" onClick={() => { setEditingProject(project); setMessage(""); setError(""); }}>Edit</button>
                        <button type="button" className="admin-button-danger" onClick={() => handleDelete(project)}>Delete</button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
